import React, { createContext, useContext } from 'react';

// DialogProvider / useDialogContext
//
// Usage:
// 1) Wrap your application (near the root) with <DialogProvider> so the dialog renders centrally:
//    <DialogProvider>
//      <App />
//    </DialogProvider>
//
// 2) From any component, call the hook to show/hide dialogs:
//    const { show, hide } = useDialogContext();
//    show({ header: 'Hello', content: <div>Body</div>, footer: <BPButton onClick={() => hide()}>Close</BPButton> });
//
// The provider renders the Dialog once; the show() call updates the content and opens it.

import useDialog, { DialogProps, UseDialogReturn } from './useDialog';

type DialogContextValue = {
  show: (props?: DialogProps) => void;
  hide: () => void;
  isOpen: boolean;
};

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

export const DialogProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { show, hide, Dialog, isOpen } = useDialog();

  return (
    <DialogContext.Provider value={{ show, hide, isOpen }}>
      {children}
      {/* Render Dialog once at the root so any consumer can call show() to display it */}
      <Dialog />
    </DialogContext.Provider>
  );
};

export function useDialogContext(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error('useDialogContext must be used within a DialogProvider');
  }
  return ctx;
}

export default DialogContext;
